import React, { useState, useEffect } from "react";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { Label } from "@shared/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";

import { getDataTypeConfig } from "../config/DataTypeConfig";
import { Popover, PopoverContent, PopoverTrigger } from "@shared/ui/popover";
import { Calendar as CalendarComponent } from "@shared/ui/calendar";
import { Checkbox } from "@shared/ui/checkbox";
import { format } from "date-fns";
import {
  Calendar,
  DollarSign,
  CalendarIcon,
  Check,
  ChevronsUpDown,
} from "lucide-react";
import { cn } from "@shared/lib/utils";

interface DataRelationFilterProps {
  dataType: string;
  currentFilters: Record<string, any>;
  onApplyFilters: (filters: Record<string, any>) => void;
  onResetFilters: () => void;
}

// Mock data functions for dropdown options based on column name
const getDropdownOptions = (
  dataType: string,
  column: string
): { value: string; label: string }[] => {
  // This would normally come from an API
  const columnLower = column.toLowerCase();

  // Ownership types
  if (columnLower.includes("ownership")) {
    return [
      { value: "owner", label: "Owner" },
      { value: "tenant", label: "Tenant" },
      { value: "landlord", label: "Landlord" },
    ];
  }

  // Billing/premise types
  if (columnLower.includes("billing") || columnLower.includes("premise")) {
    return [
      { value: "residential", label: "Residential" },
      { value: "commercial", label: "Commercial" },
      { value: "industrial", label: "Industrial" },
    ];
  }

  // Status types
  if (columnLower.includes("status")) {
    if (
      columnLower.includes("operating") ||
      columnLower.includes("operational")
    ) {
      return [
        { value: "operational", label: "Operational" },
        { value: "under_maintenance", label: "Under Maintenance" },
        { value: "non_operational", label: "Non-Operational" },
        { value: "planned", label: "Planned" },
      ];
    }
    if (columnLower.includes("backup")) {
      return [
        { value: "available", label: "Available" },
        { value: "not_available", label: "Not Available" },
        { value: "partial", label: "Partial" },
      ];
    }
    return [
      { value: "active", label: "Active" },
      { value: "inactive", label: "Inactive" },
      { value: "suspended", label: "Suspended" },
    ];
  }

  // Plan types
  if (columnLower.includes("plan")) {
    return [
      { value: "standard", label: "Standard Plan" },
      { value: "premium", label: "Premium Plan" },
      { value: "basic", label: "Basic Plan" },
    ];
  }

  // Category types
  if (columnLower.includes("category")) {
    if (columnLower.includes("sub")) {
      return [
        { value: "single_family", label: "Single Family" },
        { value: "multi_family", label: "Multi Family" },
        { value: "retail", label: "Retail" },
        { value: "office", label: "Office" },
      ];
    }
    return [
      { value: "residential", label: "Residential" },
      { value: "commercial", label: "Commercial" },
      { value: "industrial", label: "Industrial" },
    ];
  }

  // Utility service types
  if (columnLower.includes("utility")) {
    return [
      { value: "electricity", label: "Electricity" },
      { value: "water", label: "Water" },
      { value: "gas", label: "Gas" },
      { value: "wastewater", label: "Wastewater" },
    ];
  }

  // Meter types
  if (columnLower.includes("meter") && columnLower.includes("type")) {
    return [
      { value: "smart", label: "Smart" },
      { value: "manual", label: "Manual" },
      { value: "digital", label: "Digital" },
      { value: "analog", label: "Analog" },
    ];
  }

  // Manufacturer
  if (columnLower.includes("manufacturer")) {
    return [
      { value: "abb", label: "ABB" },
      { value: "siemens", label: "Siemens" },
      { value: "schneider", label: "Schneider Electric" },
      { value: "grundfos", label: "Grundfos" },
    ];
  }

  // Payment related
  if (columnLower.includes("payment")) {
    if (columnLower.includes("mode")) {
      return [
        { value: "cash", label: "Cash" },
        { value: "credit_card", label: "Credit Card" },
        { value: "debit_card", label: "Debit Card" },
        { value: "upi", label: "UPI" },
      ];
    }
    if (columnLower.includes("channel")) {
      return [
        { value: "online", label: "Online" },
        { value: "offline", label: "Offline" },
        { value: "mobile_app", label: "Mobile App" },
        { value: "branch", label: "Branch" },
      ];
    }
    if (columnLower.includes("type")) {
      if (columnLower.includes("pay")) {
        return [
          { value: "full_payment", label: "Full Payment" },
          { value: "partial_payment", label: "Partial Payment" },
          { value: "advance", label: "Advance" },
        ];
      }
      if (columnLower.includes("sub")) {
        return [
          { value: "regular", label: "Regular" },
          { value: "late_payment", label: "Late Payment" },
          { value: "early_payment", label: "Early Payment" },
        ];
      }
      return [
        { value: "bill_payment", label: "Bill Payment" },
        { value: "deposit", label: "Deposit" },
        { value: "refund", label: "Refund" },
      ];
    }
  }

  // Asset related
  if (columnLower.includes("asset")) {
    if (columnLower.includes("class")) {
      return [
        { value: "pipe", label: "Pipe" },
        { value: "valve", label: "Valve" },
        { value: "pump", label: "Pump" },
        { value: "tank", label: "Tank" },
      ];
    }
    if (columnLower.includes("type")) {
      return [
        { value: "main_line", label: "Main Line" },
        { value: "service_line", label: "Service Line" },
        { value: "gate_valve", label: "Gate Valve" },
        { value: "check_valve", label: "Check Valve" },
      ];
    }
  }

  // Facility related
  if (columnLower.includes("facility")) {
    if (columnLower.includes("type")) {
      return [
        { value: "treatment_plant", label: "Treatment Plant" },
        { value: "pumping_station", label: "Pumping Station" },
        { value: "reservoir", label: "Reservoir" },
      ];
    }
    if (columnLower.includes("manager")) {
      return [
        { value: "john_smith", label: "John Smith" },
        { value: "sarah_johnson", label: "Sarah Johnson" },
        { value: "michael_brown", label: "Michael Brown" },
      ];
    }
    if (columnLower.includes("id")) {
      return [
        { value: "FAC-001", label: "FAC-001" },
        { value: "FAC-002", label: "FAC-002" },
        { value: "FAC-003", label: "FAC-003" },
      ];
    }
  }

  // Service zone
  if (columnLower.includes("zone") || columnLower.includes("division")) {
    return [
      { value: "north_zone", label: "North Zone" },
      { value: "south_zone", label: "South Zone" },
      { value: "east_zone", label: "East Zone" },
      { value: "west_zone", label: "West Zone" },
      { value: "central_division", label: "Central Division" },
    ];
  }

  // Operational hours
  if (columnLower.includes("operational") && columnLower.includes("hours")) {
    return [
      { value: "24_7", label: "24/7" },
      { value: "6am_10pm", label: "6 AM - 10 PM" },
      { value: "8_hours", label: "8 Hours/Day" },
      { value: "business_hours", label: "Business Hours" },
    ];
  }

  // Depreciation
  if (columnLower.includes("depreciation")) {
    return [
      { value: "straight_line", label: "Straight Line" },
      { value: "declining_balance", label: "Declining Balance" },
      { value: "units_of_production", label: "Units of Production" },
    ];
  }

  // System/Network types
  if (columnLower.includes("system") && columnLower.includes("type")) {
    return [
      { value: "distribution", label: "Distribution" },
      { value: "pumping", label: "Pumping" },
      { value: "treatment", label: "Treatment" },
      { value: "storage", label: "Storage" },
    ];
  }

  if (columnLower.includes("network")) {
    if (columnLower.includes("type")) {
      return [
        { value: "distribution", label: "Distribution" },
        { value: "transmission", label: "Transmission" },
        { value: "collection", label: "Collection" },
        { value: "supply", label: "Supply" },
      ];
    }
    if (columnLower.includes("operator")) {
      return [
        { value: "operations_team_a", label: "Operations Team A" },
        { value: "operations_team_b", label: "Operations Team B" },
        { value: "john_smith", label: "John Smith" },
      ];
    }
  }

  // Data type (for asset classification)
  if (column === "Data Type") {
    return [
      { value: "text", label: "Text" },
      { value: "number", label: "Number" },
      { value: "date", label: "Date" },
      { value: "boolean", label: "Boolean" },
    ];
  }

  // Unit
  if (columnLower.includes("unit")) {
    return [
      { value: "inches", label: "Inches" },
      { value: "millimeters", label: "Millimeters" },
      { value: "feet", label: "Feet" },
      { value: "meters", label: "Meters" },
    ];
  }

  // Default options
  return [
    { value: "option1", label: "Option 1" },
    { value: "option2", label: "Option 2" },
    { value: "option3", label: "Option 3" },
  ];
};

// Get multi-select options for zipcode
const getMultiSelectOptions = (
  column: string
): { value: string; label: string }[] => {
  if (column.toLowerCase().includes("zipcode")) {
    // Generate sample zipcodes
    return Array.from({ length: 20 }, (_, i) => ({
      value: `1000${i}`,
      label: `1000${i}`,
    }));
  }
  return [];
};

export const DataRelationFilter: React.FC<DataRelationFilterProps> = ({
  dataType,
  currentFilters,
  onApplyFilters,
  onResetFilters,
}) => {
  const [localFilters, setLocalFilters] = useState<Record<string, any>>({});
  const config = getDataTypeConfig(dataType);

  useEffect(() => {
    // ✅ Initialize multi-select fields as arrays if needed
    const initialFilters = { ...currentFilters };

    config.filterColumns.forEach((filterConfig) => {
      if (
        filterConfig.type === "multi-select" &&
        !initialFilters[filterConfig.column]
      ) {
        initialFilters[filterConfig.column] = [];
      }
    });

    setLocalFilters(initialFilters);
  }, [currentFilters, config.filterColumns]);

  const handleFilterChange = (key: string, value: any) => {
    setLocalFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleApply = () => {
    onApplyFilters(localFilters);
  };

  const handleReset = () => {
    setLocalFilters({});
    onResetFilters();
  };

  const renderFilter = (filterConfig: any) => {
    const value = localFilters[filterConfig.column] || "";

    switch (filterConfig.type) {
      case "dropdown":
        const dropdownOptions = getDropdownOptions(
          dataType,
          filterConfig.column
        );
        return (
          <div key={filterConfig.column} className="space-y-2">
            <Label htmlFor={filterConfig.column}>{filterConfig.label}</Label>
            <Select
              value={value}
              onValueChange={(val) =>
                handleFilterChange(filterConfig.column, val)
              }
            >
              <SelectTrigger id={filterConfig.column}>
                <SelectValue placeholder={`Select ${filterConfig.label}`} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {dropdownOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case "date":
        return (
          <div key={filterConfig.column} className="space-y-2">
            <Label className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              {filterConfig.label}
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !value && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {value ? (
                    format(new Date(value), "PPP")
                  ) : (
                    <span>Pick a date</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="single"
                  selected={value ? new Date(value) : undefined}
                  onSelect={(date) =>
                    handleFilterChange(
                      filterConfig.column,
                      date ? format(date, "yyyy-MM-dd") : ""
                    )
                  }
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        );

      case "multi-select":
        const multiOptions = getMultiSelectOptions(filterConfig.column);
        const selectedValues = Array.isArray(value) ? value : [];

        return (
          <div key={filterConfig.column} className="space-y-2">
            <Label>{filterConfig.label}</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className={cn(
                    "w-full justify-between",
                    selectedValues.length === 0 && "text-muted-foreground"
                  )}
                >
                  {selectedValues.length > 0
                    ? `${selectedValues.length} selected`
                    : `Select ${filterConfig.label}`}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0" align="start">
                <div className="max-h-64 overflow-y-auto p-2">
                  {multiOptions.map((option) => {
                    const isSelected = selectedValues.includes(option.value);
                    return (
                      <div
                        key={option.value}
                        className="flex items-center space-x-2 p-2 hover:bg-gray-100 rounded cursor-pointer"
                        onClick={() => {
                          const newValues = isSelected
                            ? selectedValues.filter((v) => v !== option.value)
                            : [...selectedValues, option.value];
                          handleFilterChange(filterConfig.column, newValues);
                        }}
                      >
                        <Checkbox checked={isSelected} />
                        <span className="text-sm">{option.label}</span>
                        {isSelected && <Check className="ml-auto h-4 w-4" />}
                      </div>
                    );
                  })}
                </div>
              </PopoverContent>
            </Popover>
            {selectedValues.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {selectedValues.map((val) => {
                  const option = multiOptions.find((opt) => opt.value === val);
                  return (
                    <span
                      key={val}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs"
                    >
                      {option?.label || val}
                      <button
                        onClick={() => {
                          const newValues = selectedValues.filter(
                            (v) => v !== val
                          );
                          handleFilterChange(filterConfig.column, newValues);
                        }}
                        className="hover:text-blue-900"
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        );

      case "range":
        const isAmountField =
          filterConfig.column.toLowerCase().includes("amount") ||
          filterConfig.column.toLowerCase().includes("cost") ||
          filterConfig.column.toLowerCase().includes("budget");
        return (
          <div key={filterConfig.column} className="space-y-2">
            <Label className="flex items-center gap-2">
              {isAmountField && <DollarSign className="h-4 w-4" />}
              {filterConfig.label}
            </Label>
            <div className="flex gap-2">
              <Input
                type="number"
                placeholder={isAmountField ? "Min amount" : "Min"}
                value={value?.min || ""}
                onChange={(e) =>
                  handleFilterChange(filterConfig.column, {
                    ...value,
                    min: e.target.value,
                  })
                }
                className="flex-1"
              />
              <span className="self-center text-gray-500">to</span>
              <Input
                type="number"
                placeholder={isAmountField ? "Max amount" : "Max"}
                value={value?.max || ""}
                onChange={(e) =>
                  handleFilterChange(filterConfig.column, {
                    ...value,
                    max: e.target.value,
                  })
                }
                className="flex-1"
              />
            </div>
            {filterConfig.column.includes("Capacity") && (
              <p className="text-xs text-gray-500">
                {filterConfig.column.includes("MLD")
                  ? "Million Liters per Day"
                  : "Units"}
              </p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const hasFilters = config.filterColumns.length > 0;

  if (!hasFilters) {
    return (
      <div className="p-4 text-center text-gray-500">
        <p>No filters available for this data type</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      <div className="text-sm font-medium text-gray-700 mb-3">
        Filter {dataType.charAt(0).toUpperCase() + dataType.slice(1)} Data
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {config.filterColumns.map(renderFilter)}
      </div>
      <div className="flex gap-2 pt-4 border-t">
        <Button onClick={handleApply} className="flex-1">
          Apply Filters
        </Button>
        <Button onClick={handleReset} variant="outline" className="flex-1">
          Reset All
        </Button>
      </div>
    </div>
  );
};
